import { useCallback, useEffect, useState } from 'react';

import { getCollectionPackages } from '../services/packages';
import { normalizeId } from '../utils/ids';
import { useSessionUserId } from '../utils/session';

/**
 * The Discover bundles a collection holds (keepr-api KPR-1130, GET
 * /api/collections/{id}/packages): what the Card Library groups its cards by.
 *
 * Read once per collection when the library opens, and again on `refresh`
 * (after a card is deleted there, which can empty a group or install an
 * add-on's absence). Nothing is cached across opens: the answer follows
 * installs, updates and deletes made anywhere, and a stale group header that
 * offers an action the server would refuse is worse than a request.
 *
 * `loaded` is "a read has answered for THIS collection" — and a refusal counts:
 * a 403/404 (an API older than the endpoint, a grant that reaches only some
 * cards, a collection that is not saved yet) is "no groups", never an error,
 * so the library lists its cards flat as it always did. Any other failure
 * (network, 5xx) is `error` with `loaded` false until Retry succeeds, so a
 * page never draws ungrouped cards as if that were the answer.
 *
 * Returns `{ packages, loaded, loading, error, refresh }`; `packages` is a
 * stable array.
 */
const EMPTY = Object.freeze([]);
const REFUSED = new Set([400, 403, 404]);

export default function useCollectionPackages(collectionId) {
	const id = normalizeId(collectionId);
	const session = useSessionUserId();
	const [state, setState] = useState({ key: null, packages: EMPTY, loaded: false, error: false });
	const [again, setAgain] = useState(0);
	const key = id ? `${session || ''}:${id}` : null;

	useEffect(() => {
		if (!key) return undefined;
		let live = true;
		setState(prev => (prev.key === key ? { ...prev, error: false } : { key, packages: EMPTY, loaded: false, error: false }));
		getCollectionPackages(id)
			.then((packages) => { if (live) setState({ key, packages, loaded: true, error: false }); })
			.catch((err) => {
				if (!live) return;
				if (REFUSED.has(err?.response?.status)) setState({ key, packages: EMPTY, loaded: true, error: false });
				else setState(prev => ({ key, packages: prev.key === key ? prev.packages : EMPTY, loaded: prev.key === key && prev.loaded, error: true }));
			});
		return () => { live = false; };
	}, [key, id, again]);

	const refresh = useCallback(() => setAgain(n => n + 1), []);

	// A collection that is not saved has nothing installed.
	if (!key) return { packages: EMPTY, loaded: true, loading: false, error: false, refresh };
	const current = state.key === key;
	return {
		packages: current ? state.packages : EMPTY,
		loaded: current && state.loaded,
		loading: !current || (!state.loaded && !state.error),
		error: current && state.error,
		refresh,
	};
}
